/**
 * TanStack Query hooks and key factory for notification operations.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query"
import { daeApiService } from "@/services/daeApi"

const NOTIFICATIONS_PAGE_SIZE = 100

const notificationKeys = {
  all: ["notifications"] as const,
  lists: () => [...notificationKeys.all, "list"] as const,
  list: (daeId: string | undefined) => [...notificationKeys.lists(), daeId ?? null] as const,
  full: (daeId: string | undefined) =>
    [...notificationKeys.lists(), "full", daeId ?? null] as const,
  summaries: () => [...notificationKeys.all, "summary"] as const,
  summary: (daeId: string | undefined) => [...notificationKeys.summaries(), daeId ?? null] as const,
  summaryWithTotal: (daeId: string | undefined) =>
    [...notificationKeys.summaries(), "withTotal", daeId ?? null] as const,
}

/**
 * Bell summary poll: unreadCount + latestId, polled every 10s. It omits the expensive
 * full-partition totalCount, so the steady-state poll stays cheap. The bell badge reads
 * unreadCount; the context fetches the item list only when latestId changes.
 */
export function useNotificationSummary(daeId: string | undefined) {
  return useQuery({
    queryKey: notificationKeys.summary(daeId),
    queryFn: () => daeApiService.getNotificationSummary(),
    enabled: !!daeId,
    refetchInterval: 10_000,
  })
}

/**
 * Notifications-page summary: includeTotal=true, so it also returns totalCount (a full-partition
 * count). Kept on its own key and NOT on the 10s poll cadence - only the page needs the total, and
 * only while it is open.
 */
export function useNotificationSummaryWithTotal(daeId: string | undefined) {
  return useQuery({
    queryKey: notificationKeys.summaryWithTotal(daeId),
    queryFn: () => daeApiService.getNotificationSummary(true),
    enabled: !!daeId,
  })
}

/**
 * Shared useInfiniteQuery config for the notification list. The bell and the Notifications page use
 * it under distinct keys (so a full load on the page never turns the bell into an all-pages fetch);
 * only the queryKey differs.
 */
function notificationsInfiniteQuery(
  queryKey: readonly unknown[],
  daeId: string | undefined,
  pageSize: number,
) {
  return {
    queryKey,
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      daeApiService.getNotificationPage({ limit: pageSize, nextToken: pageParam ?? undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: { nextToken?: string }) => lastPage.nextToken ?? null,
    enabled: !!daeId,
  }
}

/**
 * Bell item query: page 1 only, no polling; refetched by the context when latestId changes.
 */
export function useNotificationsList(
  daeId: string | undefined,
  pageSize = NOTIFICATIONS_PAGE_SIZE,
) {
  return useInfiniteQuery(notificationsInfiniteQuery(notificationKeys.list(daeId), daeId, pageSize))
}

/**
 * Notifications page query: loads every page once (full list + mark-all), no polling.
 */
export function useAllNotifications(daeId: string | undefined, pageSize = NOTIFICATIONS_PAGE_SIZE) {
  return useInfiniteQuery(notificationsInfiniteQuery(notificationKeys.full(daeId), daeId, pageSize))
}

/**
 * Marks one or more notifications as read and optimistically updates the cache.
 */
export function useMarkNotificationsRead(daeId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (notificationIds: string[]) =>
      daeApiService.markNotificationsAsRead(notificationIds),
    onMutate: async (notificationIds) => {
      type Page = Awaited<ReturnType<typeof daeApiService.getNotificationPage>>
      type Summary = Awaited<ReturnType<typeof daeApiService.getNotificationSummary>>
      const listKeys = [notificationKeys.list(daeId), notificationKeys.full(daeId)]
      const summaryKeys = [
        notificationKeys.summary(daeId),
        notificationKeys.summaryWithTotal(daeId),
      ]
      const idSet = new Set(notificationIds)
      const previous: Array<[readonly unknown[], unknown]> = []

      // Collect the union of ids that are actually UNREAD across both caches, so the badge
      // decrement matches the true count even when the caches hold disjoint views of the ids.
      const actuallyUnread = new Set<string>()
      for (const key of listKeys) {
        await queryClient.cancelQueries({ queryKey: key })
        const prev = queryClient.getQueryData<InfiniteData<Page>>(key)
        previous.push([key, prev])
        for (const n of prev?.pages.flatMap((p) => p.notifications) ?? []) {
          if (idSet.has(n.notification_id) && n.read_status === "UNREAD") {
            actuallyUnread.add(n.notification_id)
          }
        }
        queryClient.setQueryData<InfiniteData<Page>>(key, (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  notifications: page.notifications.map((n) =>
                    idSet.has(n.notification_id) ? { ...n, read_status: "READ" } : n,
                  ),
                })),
              }
            : old,
        )
      }
      const newlyRead = actuallyUnread.size

      for (const summaryKey of summaryKeys) {
        await queryClient.cancelQueries({ queryKey: summaryKey })
        const prevSummary = queryClient.getQueryData<Summary>(summaryKey)
        previous.push([summaryKey, prevSummary])
        queryClient.setQueryData<Summary>(summaryKey, (old) =>
          old ? { ...old, unreadCount: Math.max(0, old.unreadCount - newlyRead) } : old,
        )
      }

      return { previous }
    },
    onError: (_err, _vars, context) => {
      for (const [key, prev] of context?.previous ?? []) {
        if (prev) queryClient.setQueryData(key, prev)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: notificationKeys.summaries() })
    },
  })
}
